/**
 * A category label in the planner's words (production audit 2, G).
 *
 * A category axis carries the strings the spec DECLARES, and a declared order is a contract: the
 * strings align the series and the export, so they stay as they are. But a week is declared by its
 * ISO start («2026-09-28») and a run by its minute («2026-10-03 06:04»), and drawn as they are they
 * read as codes in the axis, the tooltip and the table view. This is the one place that turns those
 * two shapes into a date (and a time, in UTC like every run's moment) in the planner's format.
 *
 * Anything else — a size, a store, a family, an ordinal word — is returned unchanged, and so is
 * a label carrying a position suffix («… (2)», the axis's last resort for two runs of one minute).
 * Pure.
 */

import { formatDate, formatDateTime } from '../lib/format';

const ISO_DATE = /^(\d{4}-\d{2}-\d{2})(?: (\(\d+\)))?$/;
const ISO_MINUTE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?: (\(\d+\)))?$/;

/** Whether `label` is one of the two shapes {@link categoryWords} rewrites. */
export function isDateLabel(label: string): boolean {
  return ISO_DATE.test(label) || ISO_MINUTE.test(label);
}

export function categoryWords(label: string, locale: string): string {
  const date = ISO_DATE.exec(label);
  if (date !== null) {
    const moment = new Date(`${date[1] as string}T00:00:00Z`);
    if (Number.isNaN(moment.getTime())) return label;
    return `${formatDate(moment.toISOString(), locale, 'UTC')}${date[2] === undefined ? '' : ` ${date[2]}`}`;
  }
  const minute = ISO_MINUTE.exec(label);
  if (minute !== null) {
    const moment = new Date(`${minute[1] as string}T${minute[2] as string}:00Z`);
    if (Number.isNaN(moment.getTime())) return label;
    return `${formatDateTime(moment.toISOString(), locale, 'UTC')}${minute[3] === undefined ? '' : ` ${minute[3]}`}`;
  }
  return label;
}
